import type { ReactNode } from 'react';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export const checkOptionVariants = cva([
  /*
   * The border is always drawn and only changes colour, so ticking an option
   * never moves the list under the pointer.
   */
  'flex w-full items-center gap-3 rounded-md border border-line bg-transparent px-3.5 py-3 text-left transition',
  'hover:border-ink-muted',
  'data-checked:border-ink data-checked:bg-fill-faint',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  'data-disabled:pointer-events-none data-disabled:opacity-40',
]);

export type CheckOptionVariants = VariantProps<typeof checkOptionVariants>;

export interface CheckOptionProps extends Omit<
  BaseCheckbox.Root.Props,
  'style' | 'className' | 'children'
> {
  /**
   * What the option says. The row is the control, so this is not a label
   * element — and a node rather than a string, because an option is sometimes a
   * person: a face, a name and a count read as one line.
   */
  label: ReactNode;
  className?: string;
}

/**
 * One option in a short list where several may be true at once — the kinds of
 * conversation Wundo should prepare, say.
 *
 * The row itself is the control, which is what makes the whole bordered
 * rectangle clickable: a `<label>` around it would not forward a click, because
 * what sits underneath is a button rather than a native input.
 *
 * Its mark is drawn here rather than borrowed from `Checkbox` on purpose. That
 * box means "this is dealt with" — a topic raised, an action point done — and
 * disappears from the page once the work does. This one is a standing choice
 * that keeps applying long after the screen is closed, which is the meaning a
 * Switch carries; it is drawn as a tick box only because several of them are
 * true at once and switches do not read as a set.
 */
export function CheckOption({ label, className, ...props }: CheckOptionProps) {
  return (
    <BaseCheckbox.Root
      data-slot='check-option'
      className={cn('group', checkOptionVariants(), className)}
      {...props}
    >
      <span
        data-slot='check-option-mark'
        aria-hidden='true'
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-xs border-2 border-line text-white transition',
          'group-data-checked:border-primary group-data-checked:bg-primary group-data-checked:ring-1 group-data-checked:ring-selected-edge',
        )}
      >
        <BaseCheckbox.Indicator className='flex items-center justify-center'>
          <Check className='size-3' strokeWidth={3.5} />
        </BaseCheckbox.Indicator>
      </span>
      <span className='min-w-0 flex-1 text-base font-bold text-ink'>{label}</span>
    </BaseCheckbox.Root>
  );
}
